import React, { useState, useEffect, useCallback } from 'react';
import { Home, Camera, Trash2, RefreshCw } from 'lucide-react';
import submitCgScrapDeliveryNote, {
  fetchNextReference,
  CALOR_REFERENCE,
  DELIVERY_ADDRESS,
  COLLECTION_ADDRESS,
} from './cgScrapDeliveryNoteEmailService';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import { getLondonDateKey } from '../utils/londonDate';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const EMPTY_TIME = { hour: '', minute: '', period: '' };

const PHOTO_MAX_EDGE = 2000;
const PHOTO_JPEG_QUALITY = 0.85;

const getLondonTime12h = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/London',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const part = (type) => parts.find((p) => p.type === type)?.value || '';
  return { hour: part('hour'), minute: part('minute'), period: part('dayPeriod').toUpperCase() };
};

const isTimeComplete = (time) => Boolean(time.hour && time.minute && time.period);
const formatTime12h = (time) => `${time.hour}:${time.minute} ${time.period}`;

// The ticket is embedded in the PDF, which only accepts JPEG or PNG, so every
// photo is re-encoded as a JPEG (this also keeps phone photos under upload limits).
const prepareTicketPhoto = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const sourceWidth = img.naturalWidth || img.width;
    const sourceHeight = img.naturalHeight || img.height;
    const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sourceWidth * scale);
    canvas.height = Math.round(sourceHeight * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to process this photo. Please retake the weighbridge ticket photo.'));
        return;
      }
      resolve(new File([blob], 'weighbridge-ticket.jpg', { type: 'image/jpeg', lastModified: Date.now() }));
    }, 'image/jpeg', PHOTO_JPEG_QUALITY);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Unable to read this photo. Please retake the weighbridge ticket photo.'));
  };
  img.src = url;
});

const TwelveHourTimeInput = ({ id, value, onChange }) => (
  <div className="grid grid-cols-3 gap-2">
    <select
      id={id}
      required
      aria-label="Hour"
      value={value.hour}
      onChange={(e) => onChange({ ...value, hour: e.target.value })}
      className="block w-full pvs-select"
    >
      <option value="">Hour</option>
      {HOURS.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
    </select>
    <select
      required
      aria-label="Minute"
      value={value.minute}
      onChange={(e) => onChange({ ...value, minute: e.target.value })}
      className="block w-full pvs-select"
    >
      <option value="">Min</option>
      {MINUTES.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
    </select>
    <select
      required
      aria-label="AM or PM"
      value={value.period}
      onChange={(e) => onChange({ ...value, period: e.target.value })}
      className="block w-full pvs-select"
    >
      <option value="">AM/PM</option>
      <option value="AM">AM</option>
      <option value="PM">PM</option>
    </select>
  </div>
);

const CGScrapDeliveryNoteForm = ({ onBack }) => {
  const [formData, setFormData] = useState(() => ({
    driverName: getCurrentUserDisplayName(),
    deliveryDate: getLondonDateKey(),
    timeArrived: EMPTY_TIME,
    timeLeft: getLondonTime12h(),
    loadDescription: '',
  }));
  const [ticket, setTicket] = useState(null);
  const [ticketProcessing, setTicketProcessing] = useState(false);
  const [reference, setReference] = useState('');
  const [referenceError, setReferenceError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [issuedReference, setIssuedReference] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const loadReference = useCallback(async () => {
    setReferenceError('');
    try {
      setReference(await fetchNextReference());
    } catch (error) {
      console.error('Failed to load PVS reference number:', error);
      setReference('');
      setReferenceError(error.message || 'Unable to load the PVS reference number.');
    }
  }, []);

  useEffect(() => {
    loadReference();
  }, [loadReference]);

  useEffect(() => () => {
    if (ticket?.previewUrl) URL.revokeObjectURL(ticket.previewUrl);
  }, [ticket]);

  const updateField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const showError = (message) => {
    setSubmitMessage(message);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTicketCapture = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSubmitMessage('');
    setTicketProcessing(true);
    try {
      const prepared = await prepareTicketPhoto(file);
      setTicket({ file: prepared, previewUrl: URL.createObjectURL(prepared) });
    } catch (error) {
      showError(error.message);
    } finally {
      setTicketProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setSubmitMessage('');

    if (!reference) {
      showError('The PVS reference number could not be loaded. Tap retry next to the reference before submitting.');
      return;
    }
    if (!formData.driverName.trim() || !formData.deliveryDate || !formData.loadDescription.trim()) {
      showError('All fields are mandatory. Please complete the form before submitting.');
      return;
    }
    if (!isTimeComplete(formData.timeArrived) || !isTimeComplete(formData.timeLeft)) {
      showError('Enter both the time arrived on site and the time leaving site.');
      return;
    }
    if (!ticket) {
      showError('Add a photo of the weighbridge ticket before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const issued = await submitCgScrapDeliveryNote(
        {
          driverName: formData.driverName.trim(),
          deliveryDate: formData.deliveryDate,
          timeArrived: formatTime12h(formData.timeArrived),
          timeLeft: formatTime12h(formData.timeLeft),
          loadDescription: formData.loadDescription.trim(),
          weighbridgeTicket: ticket.file,
        },
        reference,
        { onReferenceChange: setReference },
      );
      setIssuedReference(issued);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      showError(error.message || 'Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-3xl font-bold text-white">CG Scrap Delivery Note</h1>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Calor Gas scrap vessel delivery to EMR Bradford</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {submitMessage && (
              <div className="p-5 rounded-lg border-2 shadow-md bg-red-900/30 text-red-400 border-red-500/30">
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="driverName" className="block text-sm font-medium text-pvs-text-muted mb-2">Driver Name *</label>
                <input
                  id="driverName"
                  type="text"
                  required
                  value={formData.driverName}
                  onChange={(e) => updateField('driverName', e.target.value)}
                  className="block w-full pvs-input"
                />
              </div>
              <div>
                <label htmlFor="deliveryDate" className="block text-sm font-medium text-pvs-text-muted mb-2">Delivery Date *</label>
                <input
                  id="deliveryDate"
                  type="date"
                  required
                  value={formData.deliveryDate}
                  onChange={(e) => updateField('deliveryDate', e.target.value)}
                  className="block w-full pvs-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Calor Reference No</label>
                <input
                  type="text"
                  value={CALOR_REFERENCE}
                  readOnly
                  className="block w-full pvs-input opacity-80 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">PVS Reference No</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reference || (referenceError ? 'Unavailable' : 'Loading…')}
                    readOnly
                    className="block w-full pvs-input opacity-80 cursor-not-allowed font-semibold"
                  />
                  {referenceError && (
                    <button
                      type="button"
                      onClick={loadReference}
                      className="flex items-center justify-center px-3 border border-pvs-accent/40 rounded-md text-pvs-accent hover:bg-pvs-accent/10"
                      title="Retry loading reference"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {referenceError && <p className="mt-1 text-xs text-red-400">{referenceError}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Delivery Address</label>
                <textarea
                  value={DELIVERY_ADDRESS.join('\n')}
                  readOnly
                  rows={4}
                  className="block w-full pvs-input opacity-80 cursor-not-allowed resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Collection Address</label>
                <textarea
                  value={COLLECTION_ADDRESS.join('\n')}
                  readOnly
                  rows={4}
                  className="block w-full pvs-input opacity-80 cursor-not-allowed resize-none"
                />
              </div>
            </div>

            <div>
              <label htmlFor="loadDescription" className="block text-sm font-medium text-pvs-text-muted mb-2">Load Description *</label>
              <textarea
                id="loadDescription"
                required
                rows={4}
                value={formData.loadDescription}
                onChange={(e) => updateField('loadDescription', e.target.value)}
                placeholder="Describe the tank size and quantity of each size delivered"
                className="block w-full pvs-input placeholder:italic"
              />
            </div>

            <div>
              <span className="block text-sm font-medium text-pvs-text-muted mb-2">Weighbridge Ticket *</span>
              <input
                id="weighbridgeTicket"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleTicketCapture}
                className="hidden"
              />
              {ticket ? (
                <div className="flex items-center gap-4 p-3 bg-pvs-card-light rounded-lg border border-pvs-border">
                  <img src={ticket.previewUrl} alt="Weighbridge ticket" className="w-24 h-24 object-cover rounded" />
                  <div className="flex-1 flex flex-wrap gap-2">
                    <label
                      htmlFor="weighbridgeTicket"
                      className="inline-flex items-center gap-2 px-3 py-2 border border-pvs-accent/40 rounded-md text-sm font-medium text-pvs-accent hover:bg-pvs-accent/10 cursor-pointer"
                    >
                      <Camera className="w-4 h-4" />
                      Retake
                    </label>
                    <button
                      type="button"
                      onClick={() => setTicket(null)}
                      className="inline-flex items-center gap-2 px-3 py-2 border border-red-500/40 rounded-md text-sm font-medium text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="weighbridgeTicket"
                  className="flex flex-col items-center justify-center border-2 border-dashed border-pvs-border rounded-lg p-6 text-center hover:border-pvs-accent transition-colors cursor-pointer"
                >
                  <Camera className="w-10 h-10 text-pvs-text-dim mb-3" />
                  <span className="text-pvs-text-muted font-medium">
                    {ticketProcessing ? 'Processing photo…' : 'Add weighbridge ticket'}
                  </span>
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="timeArrived" className="block text-sm font-medium text-pvs-text-muted mb-2">Time Arrived On Site *</label>
                <TwelveHourTimeInput
                  id="timeArrived"
                  value={formData.timeArrived}
                  onChange={(value) => updateField('timeArrived', value)}
                />
              </div>
              <div>
                <label htmlFor="timeLeft" className="block text-sm font-medium text-pvs-text-muted mb-2">Time Leaving Site *</label>
                <TwelveHourTimeInput
                  id="timeLeft"
                  value={formData.timeLeft}
                  onChange={(value) => updateField('timeLeft', value)}
                />
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                <Home className="h-4 w-4" />
                Back to Forms
              </button>

              <button
                type="submit"
                disabled={isSubmitting || ticketProcessing || !reference}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Delivery Note'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <FormSuccessModal
        isOpen={showSuccessModal}
        formName={`CG Scrap Delivery Note ${issuedReference}`}
        onClose={() => {
          setShowSuccessModal(false);
          onBack?.();
        }}
      />
    </PVSPageLayout>
  );
};

export default CGScrapDeliveryNoteForm;
